import type { InvoiceVariableGroup } from '@metycle/shared';

// ── The document-shaped invoice field model: types ──────────────────────────
//
// One registry (invoice-field-registry.ts) describes every field an operator
// sees on an invoice form: its canonical key, form/payload key, label,
// document group and position, where its value comes from, where it persists,
// and per-kind/per-side presence and editability. The per-surface field lists
// (create/edit dialogs, review dialog, detail drawer) converge on this model
// instead of each carrying its own divergent copy.
//
// The vocabulary builds on the shared namespace: a field that renders on the
// document keys onto its `INVOICE_VARIABLE_NAMESPACE` path (asserted in the
// model spec), and the override-persisted partition mirrors
// `INVOICE_EDITABLE_KEYS` exactly — the server-side allow-set stays the
// boundary; this model only decides what forms surface and how.
//
// Deliberately NOT in the registry: workflow meta-controls that are not
// document fields (the edit-mode reclassify type/reason pair) and the
// lifecycle-owned display fields the detail drawer derives from row state
// (legal number, formal-issue stamp, expected due date). Editability here is
// the per-kind/per-side rule for a Draft; the Draft→Issued freeze is
// lifecycle state and belongs to the action model, not the field model.

export const INVOICE_KIND_KEYS = [
  'Advance',
  'Provisional',
  'Final_Settlement',
  'Credit_Note',
  'Debit_Note',
] as const;
export type InvoiceKindKey = (typeof INVOICE_KIND_KEYS)[number];

export type InvoiceSide = 'buy' | 'sell';

// Which side(s) a rule applies to: `true` = both, `false` = neither.
export type SideRule = boolean | InvoiceSide;

// Where the field's system value comes from:
//   contract — derived from the order/ticket/BL trade data (corrected upstream)
//   operator — supplied by the operator on the invoice itself
//   system   — assigned by the system (lifecycle, numbering)
export type FieldSource = 'contract' | 'operator' | 'system';

// Where an operator edit persists:
//   column   — a column on the invoice row (create/update payload)
//   override — the field_overrides map (review write, allowlist-enforced)
//   relation — an FK selection validated against a catalog (issuer bank)
//   none     — presentational or folded into another field, never its own write
export type FieldStorage = 'column' | 'override' | 'relation' | 'none';

export type FieldInput = 'text' | 'textarea' | 'number' | 'date' | 'select' | 'company' | 'lines';

export interface InvoiceFieldKindRule {
  // Editable (while Draft) on which side(s). `false` = shown read-only.
  readonly editable: SideRule;
  readonly required?: SideRule;
}

export type InvoiceFieldKinds = Readonly<Partial<Record<InvoiceKindKey, InvoiceFieldKindRule>>>;

export interface InvoiceFieldDef {
  // Canonical key — the namespace path when the field renders on the document.
  readonly key: string;
  // The create/update payload key when it differs from `key`
  // (e.g. the document's counterparty_name travels as company_name).
  readonly formKey?: string;
  readonly label: string;
  // Document group; group order and in-group order are the document layout.
  readonly group: InvoiceVariableGroup;
  readonly source: FieldSource;
  readonly storage: FieldStorage;
  readonly input: FieldInput;
  // The namespace binding this field feeds when it differs from `key`, or
  // null when the field never renders on the document (workflow/assembly
  // input).
  readonly docPath?: string | null;
  // True when the system can resolve a value for the field without operator
  // input. The override fields with no upstream source (the BL-body set) are
  // false — the document shows an em-dash until the operator fills them.
  readonly systemSourced: boolean;
  readonly kinds: InvoiceFieldKinds;
}

export function sideAllows(sideRule: SideRule | undefined, side: InvoiceSide): boolean {
  if (sideRule === undefined) return false;
  return sideRule === true || sideRule === side;
}
